import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import driverApi from '../clients/DriverClient';
import { RaceLap, TelemetryLapData } from '../models/TelemetryData';
import LapSelector from './telemetry/LapSelector';
import TelemetryCharts from './telemetry/TelemetryCharts';
import TrackMap from './telemetry/TrackMap';
import ChannelToolbar from './telemetry/ChannelToolbar';
import RaceEngineerPanel, { RaceEngineerTip, EngineerState } from './telemetry/RaceEngineerPanel';
import { AnalysisRequestEntry } from '../models/requestBodies/AnalysisRequestBody';
import StatusBar from './telemetry/StatusBar';
import StatStrip from './telemetry/StatStrip';
// DashboardNavbar hidden on telemetry page to maximize space
import { formatMilliseconds } from '../utils/time';
import { computeSectorDistances, SectorDistances } from '../utils/sectorDistances';
import './telemetry/TelemetryPage.css';
import ReferenceSelector from './telemetry/ReferenceSelector';
import { ReferenceLapsResponse, ReferenceLap } from '../models/responseBodies/ReferenceLapsResponse';
import { REF_LAP_ID } from './telemetry/LapSelector';
import { AlignMode, resolveAnchorId, loadTrackMapPrefs, saveTrackMapPrefs } from '../utils/trackMapAlignment';
import { shortName } from '../utils/shortName';
import { currentTenant } from '../tenant/tenants';

// The AI race engineer runs on CommonApi (World of Racing's data and Anthropic key) only.
const hasAiAnalysis = currentTenant.features.aiAnalysis;

interface RaceInfoState {
  track: string;
  layout: string;
  car: string;
  date: string;
  location: string;
}

const TelemetryPage: React.FC = () => {
  const { raceId, lapId } = useParams<{ raceId: string; lapId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const raceInfo = (location.state as RaceInfoState) || null;

  const [laps, setLaps] = useState<RaceLap[]>([]);
  const [checkedLapIds, setCheckedLapIds] = useState<string[]>([]);
  const [loadingLapIds, setLoadingLapIds] = useState<string[]>([]);
  const [lapDataMap, setLapDataMap] = useState<Map<string, TelemetryLapData>>(new Map());
  const [currentDistanceMarker, setCurrentDistanceMarker] = useState<number | null>(null);
  const [failedLapIds, setFailedLapIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [channels, setChannels] = useState<Record<string, boolean>>({
    brake: true,
    throttle: true,
    speed: true,
    steering: false,
    rpm: false,
    gear: false,
    delta: true,
  });

  const [expandedPanel, setExpandedPanel] = useState<'left' | 'right' | null>(null);
  const [activeTipId, setActiveTipId] = useState<number | null>(null);
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [alignMode, setAlignMode] = useState<AlignMode>(() => loadTrackMapPrefs().alignMode);
  const [anchorLapId, setAnchorLapId] = useState<string | null>(() => loadTrackMapPrefs().anchorLapId);

  const [playbackState, setPlaybackState] = useState<{
    playing: boolean;
    time: number;
    speed: 1 | 2 | 4;
    loop: boolean;
  }>({ playing: false, time: 0, speed: 1, loop: true });

  const [engineerState, setEngineerState] = useState<EngineerState>({
    status: 'idle',
    tips: [],
  });

  const [referenceLaps, setReferenceLaps] = useState<ReferenceLapsResponse | null>(null);
  const [selectedRefType, setSelectedRefType] = useState<'lapRecord' | 'personalBest' | 'driver' | null>(null);
  const [driverRef, setDriverRef] = useState<ReferenceLap | null>(null);
  const refTelemetryCache = useRef<Map<string, TelemetryLapData>>(new Map());
  const [refTelemetryLoading, setRefTelemetryLoading] = useState(false);

  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  const [isTrackMapFullscreen, setIsTrackMapFullscreen] = useState(false);
  const [mobileMapExpanded, setMobileMapExpanded] = useState(false);
  const [mobileEngineerExpanded, setMobileEngineerExpanded] = useState(false);

  const analysisAbortRef = useRef<AbortController | null>(null);
  const refTelemetryAbortRef = useRef<AbortController | null>(null);
  const isMountedRef = useRef(true);

  // Abort in-flight analysis and reference telemetry on unmount
  useEffect(() => {
    return () => {
      analysisAbortRef.current?.abort();
      refTelemetryAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handlePlaybackTick = useCallback((distanceMarker: number) => {
    setCurrentDistanceMarker(distanceMarker);
  }, []);

  const handlePlaybackStateChange = useCallback((newState: { playing: boolean; time: number; speed: 1 | 2 | 4; loop: boolean }) => {
    setPlaybackState(newState);
  }, []);

  const handleAlignModeChange = useCallback((mode: AlignMode) => {
    setAlignMode(mode);
    if (mode === 'dist') {
      setPlaybackState((prev) => ({ ...prev, playing: false }));
    }
  }, []);

  // Ref to avoid stale closure over lapDataMap in fetchLapTelemetry
  const lapDataMapRef = useRef(lapDataMap);
  lapDataMapRef.current = lapDataMap;

  // Fetch a single lap's telemetry and cache it (keyed by lap.id GUID)
  const fetchLapTelemetry = useCallback(
    async (lap: RaceLap) => {
      if (lapDataMapRef.current.has(lap.id)) return; // Already cached

      setLoadingLapIds((prev) => [...prev, lap.id]);
      try {
        const data = await driverApi.getLapTelemetry(lap.id);
        if (!isMountedRef.current) return;
        setLapDataMap((prev) => {
          const next = new Map(prev);
          next.set(lap.id, data);
          return next;
        });
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error(`Failed to load telemetry for lap ${lap.lap}:`, err);
        setFailedLapIds((prev) => [...prev, lap.id]);
        // Uncheck the failed lap
        setCheckedLapIds((prev) => prev.filter((id) => id !== lap.id));
      } finally {
        if (isMountedRef.current) {
          setLoadingLapIds((prev) => prev.filter((id) => id !== lap.id));
        }
      }
    },
    []
  );

  const loadRefTelemetry = useCallback(async (ref: ReferenceLap) => {
    // Abort any previous in-flight request
    refTelemetryAbortRef.current?.abort();
    const abortController = new AbortController();
    refTelemetryAbortRef.current = abortController;

    if (refTelemetryCache.current.has(ref.id)) {
      setLapDataMap((prev) => {
        const next = new Map(prev);
        next.set(REF_LAP_ID, refTelemetryCache.current.get(ref.id)!);
        return next;
      });
      return;
    }

    setRefTelemetryLoading(true);
    try {
      const data = await driverApi.getLapTelemetry(ref.id, ref.location);
      if (abortController.signal.aborted) return;
      refTelemetryCache.current.set(ref.id, data);
      setLapDataMap((prev) => {
        const next = new Map(prev);
        next.set(REF_LAP_ID, data);
        return next;
      });
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      console.error('Failed to load reference telemetry:', err);
      setLapDataMap((prev) => {
        const next = new Map(prev);
        next.delete(REF_LAP_ID);
        return next;
      });
    } finally {
      if (!abortController.signal.aborted) {
        setRefTelemetryLoading(false);
      }
    }
  }, []);

  const handleRefSelect = useCallback((refType: 'lapRecord' | 'personalBest') => {
    setSelectedRefType(refType);
    const ref = refType === 'lapRecord'
      ? referenceLaps?.lapRecord
      : referenceLaps?.personalBest;
    if (ref) {
      loadRefTelemetry(ref);
    }
  }, [referenceLaps, loadRefTelemetry]);

  const handleDriverRefSelect = useCallback((ref: ReferenceLap) => {
    setDriverRef(ref);
    setSelectedRefType('driver');
    loadRefTelemetry(ref);
  }, [loadRefTelemetry]);

  const handleSearchDrivers = useCallback(async (q: string) => {
    const firstLap = laps[0];
    if (!firstLap) return [];
    return driverApi.searchReferenceDrivers(
      firstLap.gameTrack,
      firstLap.gameCar,
      // Pass gameLayout through as-is. An empty string is a real Assetto layout key
      // (the base layout); collapsing it to undefined would search every layout.
      firstLap.gameLayout,
      q
    );
  }, [laps]);

  // Initial load: fetch laps, fetch selected lap telemetry, fetch reference laps in parallel
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!raceId || !lapId) return;
      setIsLoading(true);
      setError('');
      try {
        const raceLaps = await driverApi.getLapsForLap(lapId);
        if (cancelled) return;
        setLaps(raceLaps);

        if (raceLaps.length === 0) {
          setError('No laps found for this session.');
          setIsLoading(false);
          return;
        }

        // Fire reference fetch using game IDs from the laps response
        const firstLap = raceLaps[0];
        // gameLayout passed as-is: "" is the base layout, not "no layout filter".
        const refLapsPromise = driverApi.getReferenceLaps(firstLap.gameTrack, firstLap.gameCar, firstLap.gameLayout).catch((err: unknown) => {
          console.error('Failed to load reference laps:', err);
          return null;
        });

        // Find the selected lap by GUID
        const selectedLap = raceLaps.find((l) => l.id === lapId);
        if (!selectedLap) {
          if (!cancelled) { setError('Selected lap not found.'); setIsLoading(false); }
          return;
        }

        if (!cancelled) setCheckedLapIds([selectedLap.id]);

        // Fetch telemetry for the selected lap only — reference lap is the comparison
        const data = await driverApi.getLapTelemetry(selectedLap.id);
        if (cancelled) return;
        const dataMap = new Map<string, TelemetryLapData>();
        dataMap.set(selectedLap.id, data);
        setLapDataMap(dataMap);

        // Now resolve the reference laps promise (already in-flight since before getLapsForLap)
        const refData = await refLapsPromise;
        if (cancelled) return;
        if (refData) {
          setReferenceLaps(refData);
          const defaultType = refData.lapRecord ? 'lapRecord' : refData.personalBest ? 'personalBest' : null;
          setSelectedRefType(defaultType);
          const defaultRef = defaultType === 'lapRecord' ? refData.lapRecord : refData.personalBest;
          if (defaultRef) {
            try {
              const refTelemetry = await driverApi.getLapTelemetry(defaultRef.id, defaultRef.location);
              if (cancelled) return;
              refTelemetryCache.current.set(defaultRef.id, refTelemetry);
              setLapDataMap((prev) => {
                const next = new Map(prev);
                next.set(REF_LAP_ID, refTelemetry);
                return next;
              });
            } catch (err) {
              if (!cancelled) console.error('Failed to load reference telemetry:', err);
            }
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load telemetry data.');
          console.error(err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, lapId]);

  // Pause playback if track map panel is collapsed
  useEffect(() => {
    if (expandedPanel === 'right') {
      setPlaybackState((prev) => ({ ...prev, playing: false }));
    }
  }, [expandedPanel]);

  const handleToggleLap = useCallback(
    (toggleLapId: string) => {
      setCheckedLapIds((prev) => {
        if (prev.includes(toggleLapId)) {
          return prev.filter((id) => id !== toggleLapId);
        }
        return [...prev, toggleLapId];
      });
      setPlaybackState((prev) => ({ ...prev, playing: false, time: 0 }));
      const lap = laps.find((l) => l.id === toggleLapId);
      if (lap) fetchLapTelemetry(lap);
    },
    [laps, fetchLapTelemetry]
  );

  const handleToggleChannel = useCallback((channel: string) => {
    setChannels((prev) => ({ ...prev, [channel]: !prev[channel] }));
  }, []);

  const handleChartClick = useCallback((distanceMarker: number) => {
    setCurrentDistanceMarker(distanceMarker);
    setPlaybackState((prev) => ({ ...prev, playing: false }));
  }, []);

  const [zoomRange, setZoomRange] = useState<{ min: number; max: number } | null>(null);

  const handleZoomChange = useCallback((min: number | null, max: number | null) => {
    if (min != null && max != null) {
      setZoomRange({ min, max });
    } else {
      setZoomRange(null);
    }
  }, []);

  // Derive activeRef and hasActiveRef before allLapIds (which references hasActiveRef)
  const activeRef: ReferenceLap | null = useMemo(() => {
    if (selectedRefType === 'driver') return driverRef;
    if (!referenceLaps || selectedRefType === null) return null;
    return selectedRefType === 'lapRecord'
      ? referenceLaps.lapRecord
      : referenceLaps.personalBest;
  }, [referenceLaps, selectedRefType, driverRef]);

  const hasActiveRef = activeRef !== null && lapDataMap.has(REF_LAP_ID);

  // Stable lap ordering (sorted by time) for consistent color assignment
  const allLapIds = useMemo(() => {
    const sessionIds = [...laps].sort((a, b) => a.time - b.time).map((l) => l.id);
    return hasActiveRef ? [REF_LAP_ID, ...sessionIds] : sessionIds;
  }, [laps, hasActiveRef]);

  const renderLapIds = useMemo(() => {
    return hasActiveRef ? [REF_LAP_ID, ...checkedLapIds] : checkedLapIds;
  }, [checkedLapIds, hasActiveRef]);

  const resolvedAnchorId = useMemo(
    () => resolveAnchorId(anchorLapId, renderLapIds),
    [anchorLapId, renderLapIds]
  );

  useEffect(() => {
    saveTrackMapPrefs({ alignMode, anchorLapId });
  }, [alignMode, anchorLapId]);

  // Top 2 fastest checked laps for heatmap display
  const heatmapLapIds = useMemo(() => {
    if (!heatmapEnabled) return [];
    const checkedLaps = laps.filter((l) => checkedLapIds.includes(l.id));
    return checkedLaps
      .sort((a, b) => a.time - b.time)
      .slice(0, 2)
      .map((l) => l.id);
  }, [heatmapEnabled, laps, checkedLapIds]);

  const refChipLabel = useMemo(() => {
    if (selectedRefType === 'lapRecord') return 'LR';
    if (selectedRefType === 'personalBest') return 'PB';
    if (selectedRefType === 'driver' && driverRef) return shortName(driverRef.driverName);
    return '';
  }, [selectedRefType, driverRef]);

  // Build lap labels map: GUID -> "Lap N"
  const lapLabels = useMemo(() => {
    const labels = new Map<string, string>();
    for (const lap of laps) {
      labels.set(lap.id, `Lap ${lap.lap}`);
    }
    if (hasActiveRef && selectedRefType) {
      labels.set(REF_LAP_ID, refChipLabel);
    }
    return labels;
  }, [laps, hasActiveRef, selectedRefType, refChipLabel]);

  // Track length comes from the API response's `l` field
  const trackLength = useMemo(() => {
    const firstData = lapDataMap.values().next().value;
    if (!firstData) return 4060; // Default fallback
    return firstData.l;
  }, [lapDataMap]);

  const selectedLap = useMemo(() => laps.find((l) => l.id === lapId), [laps, lapId]);

  const sectorDistances = useMemo((): SectorDistances | null => {
    if (!selectedLap) return null;
    const telemetry = lapDataMap.get(selectedLap.id);
    if (!telemetry) return null;
    return computeSectorDistances(selectedLap, telemetry);
  }, [selectedLap, lapDataMap]);

  const fastestLap = useMemo(() => {
    if (laps.length === 0) return null;
    return [...laps].sort((a, b) => a.time - b.time)[0];
  }, [laps]);
  const delta = useMemo(() => {
    if (!selectedLap || !fastestLap) return null;
    return selectedLap.time - fastestLap.time;
  }, [selectedLap, fastestLap]);

  const refDelta = useMemo(() => {
    if (!selectedLap || !activeRef) return null;
    return selectedLap.time - activeRef.time;
  }, [selectedLap, activeRef]);

  const handleTipClick = useCallback((tip: RaceEngineerTip) => {
    if (activeTipId === tip.id) {
      // Second click: zoom to tip range
      handleZoomChange(tip.distanceRange.min, tip.distanceRange.max);
      setActiveTipId(null);
    } else {
      // First click: highlight
      setActiveTipId(tip.id);
    }
  }, [activeTipId, handleZoomChange]);

  const handleAnalyze = useCallback(async () => {
    // Abort any in-flight request
    if (analysisAbortRef.current) {
      analysisAbortRef.current.abort();
    }

    const abortController = new AbortController();
    analysisAbortRef.current = abortController;

    setEngineerState({ status: 'loading', tips: [] });

    try {
      const driverId = laps[0]?.driverId;
      if (!driverId || !raceId) {
        setEngineerState({ status: 'idle', tips: [] });
        return;
      }

      const entries: AnalysisRequestEntry[] = [
        {
          driverId,
          data: {
            raceId,
            lapIds: [...checkedLapIds],
          },
        },
      ];

      // Step 1: Submit analysis job
      const { jobId } = await driverApi.submitAnalysis(entries, abortController.signal);

      // Step 2: Poll for results every 1.5s
      const MAX_POLL_ATTEMPTS = 40;
      let attempts = 0;

      const poll = async (): Promise<void> => {
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setEngineerState({
            status: 'error',
            tips: [],
            error: 'Analysis timed out — try again.',
          });
          return;
        }
        attempts++;

        const result = await driverApi.getAnalysisResult(jobId, abortController.signal);

        if (result.status === 'completed') {
          setEngineerState({
            status: 'ready',
            tips: result.tips ?? [],
            analyzedLapIds: [...checkedLapIds].sort(),
          });
          return;
        }

        if (result.status === 'failed') {
          setEngineerState({
            status: 'error',
            tips: [],
            error: result.message || 'Analysis failed — try again.',
          });
          return;
        }

        // Check abort before sleeping so we exit immediately on cancel
        if (abortController.signal.aborted) return;

        // Still processing — wait and poll again
        await new Promise((resolve) => setTimeout(resolve, 1500));
        return poll();
      };

      await poll();
    } catch (err: any) {
      if (err?.name === 'AbortError') return; // Silently ignore aborted requests
      setEngineerState({
        status: 'error',
        tips: [],
        error: err?.message || 'Analysis failed — try again.',
      });
    }
  }, [laps, raceId, checkedLapIds]);

  const isAnalyzeEnabled = checkedLapIds.length > 0 && engineerState.status !== 'loading';

  const isStale = useMemo(() => {
    if (!engineerState.analyzedLapIds) return false;
    const current = [...checkedLapIds].sort().join(',');
    const analyzed = engineerState.analyzedLapIds.join(',');
    return current !== analyzed;
  }, [checkedLapIds, engineerState.analyzedLapIds]);

  if (isLoading) {
    return (
      <div className="telemetry-page">
        <div className="tele-loading">Loading telemetry data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="telemetry-page">
        <div className="tele-error">{error}</div>
      </div>
    );
  }

  return (
      <div className="telemetry-page">
        {/* Header with car name + stat pills */}
        <header className="tele-header">
          <div className="tele-header-left">
            <button className="tele-back-btn" onClick={() => navigate('/dashboard')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <div>
              <div className="tele-car-name">{raceInfo?.car || 'Race Session'}</div>
              <div className="tele-car-meta">
                {raceInfo?.track || ''}
              </div>
            </div>
          </div>
          <div className="tele-header-stats">
            <div className="tele-stat">
              <span className="tele-stat-label">Selected Lap</span>
              <span className="tele-stat-value">{selectedLap ? formatMilliseconds(selectedLap.time) : '\u2014'}</span>
            </div>

            {activeRef ? (
              <>
                <div className="tele-stat">
                  <span className="tele-stat-label">My Best</span>
                  <span className="tele-stat-value tele-stat-green">
                    {referenceLaps?.personalBest ? formatMilliseconds(referenceLaps.personalBest.time) : '\u2014'}
                  </span>
                </div>
                <ReferenceSelector
                  referenceLaps={referenceLaps}
                  selectedRefType={selectedRefType}
                  driverRef={driverRef}
                  onSelect={handleRefSelect}
                  onDriverSelect={handleDriverRefSelect}
                  searchDrivers={handleSearchDrivers}
                  isLoading={refTelemetryLoading}
                />
                <div className="tele-stat">
                  <span className="tele-stat-label">Delta to Ref</span>
                  <span className={`tele-stat-value ${refDelta !== null && refDelta > 0 ? 'tele-stat-red' : 'tele-stat-green'}`}>
                    {refDelta != null ? (refDelta === 0 ? '\u00b10.000' : `${refDelta > 0 ? '+' : ''}${formatMilliseconds(Math.abs(refDelta))}`) : '\u2014'}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className="tele-stat">
                  <span className="tele-stat-label">Fastest Lap</span>
                  <span className="tele-stat-value tele-stat-green">{fastestLap ? formatMilliseconds(fastestLap.time) : '\u2014'}</span>
                </div>
                <div className="tele-stat">
                  <span className="tele-stat-label">Delta</span>
                  <span className={`tele-stat-value ${delta && delta > 0 ? 'tele-stat-red' : 'tele-stat-green'}`}>
                    {delta != null ? (delta === 0 ? '\u00b10.000' : `${delta > 0 ? '+' : ''}${formatMilliseconds(Math.abs(delta))}`) : '\u2014'}
                  </span>
                </div>
              </>
            )}
          </div>
        </header>

        <StatStrip
          laps={laps}
          checkedLapIds={checkedLapIds}
          lapDataMap={lapDataMap}
          baselineLapId={lapId!}
          allLapIds={allLapIds}
          refLap={activeRef}
          collapsed={isMobile}
        />

        {/* 50/50 split content */}
        <div className="tele-content">
          {/* Left panel: laps + track map + race engineer */}
          <div className={`tele-panel-left${expandedPanel === 'right' ? ' collapsed' : ''}${expandedPanel === 'left' ? ' expanded' : ''}`}>
            <LapSelector
              laps={laps}
              selectedLapId={lapId!}
              checkedLapIds={checkedLapIds}
              loadingLapIds={loadingLapIds}
              failedLapIds={failedLapIds}
              onToggleLap={handleToggleLap}
              isExpanded={expandedPanel === 'left'}
              onToggleExpand={() => setExpandedPanel(expandedPanel === 'left' ? null : 'left')}
              allLapIds={allLapIds}
              refChip={hasActiveRef && activeRef ? {
                label: refChipLabel,
                time: activeRef.time,
              } : null}
              alignMode={alignMode}
              anchorLapId={resolvedAnchorId}
              onAnchorChange={setAnchorLapId}
            />
            {/* Mobile-only collapse toggle for Track Map */}
            <div
              className="mobile-panel-toggle"
              onClick={() => setMobileMapExpanded((p) => !p)}
            >
              Track Map
              <svg
                className={`mobile-panel-toggle-chevron${mobileMapExpanded ? ' open' : ''}`}
                width="10" height="10" viewBox="0 0 12 12" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              >
                <polyline points="2,4 6,8 10,4" />
              </svg>
            </div>
            {(!isMobile || mobileMapExpanded) && (
              <TrackMap
                track={laps[0]?.gameTrack || ''}
                layout={laps[0]?.gameLayout || ''}
                lapDataMap={lapDataMap}
                checkedLapIds={renderLapIds}
                currentDistanceMarker={currentDistanceMarker}
                allLapIds={allLapIds}
                zoomRange={zoomRange}
                heatmapEnabled={heatmapEnabled}
                onHeatmapToggle={setHeatmapEnabled}
                heatmapLapIds={heatmapLapIds}
                playbackState={playbackState}
                onPlaybackStateChange={handlePlaybackStateChange}
                onPlaybackTick={handlePlaybackTick}
                onFullscreenChange={setIsTrackMapFullscreen}
                alignMode={alignMode}
                anchorLapId={resolvedAnchorId}
                onAlignModeChange={handleAlignModeChange}
              />
            )}

            {hasAiAnalysis && (
              <>
                {/* Mobile-only collapse toggle for Race Engineer */}
                <div
                  className="mobile-panel-toggle"
                  onClick={() => setMobileEngineerExpanded((p) => !p)}
                >
                  AI Race Engineer
                  <svg
                    className={`mobile-panel-toggle-chevron${mobileEngineerExpanded ? ' open' : ''}`}
                    width="10" height="10" viewBox="0 0 12 12" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  >
                    <polyline points="2,4 6,8 10,4" />
                  </svg>
                </div>
                {(!isMobile || mobileEngineerExpanded) && (
                  <RaceEngineerPanel
                    engineerState={engineerState}
                    activeTipId={activeTipId}
                    onTipClick={handleTipClick}
                    onAnalyze={handleAnalyze}
                    isAnalyzeEnabled={isAnalyzeEnabled}
                    isStale={isStale}
                  />
                )}
              </>
            )}
          </div>

          {/* Right panel: channels + charts */}
          <div className={`tele-panel-right${expandedPanel === 'left' ? ' collapsed' : ''}${expandedPanel === 'right' ? ' expanded' : ''}`}>
            <ChannelToolbar
              channels={channels}
              onToggleChannel={handleToggleChannel}
              isExpanded={expandedPanel === 'right'}
              onToggleExpand={() => setExpandedPanel(expandedPanel === 'right' ? null : 'right')}
            />
            <TelemetryCharts
              lapDataMap={lapDataMap}
              checkedLapIds={renderLapIds}
              baselineLapId={lapId!}
              channels={channels}
              trackLength={trackLength}
              onChartClick={handleChartClick}
              onZoomChange={handleZoomChange}
              lapLabels={lapLabels}
              allLapIds={allLapIds}
              sectorDistances={sectorDistances}
              currentDistanceMarker={currentDistanceMarker}
              zoomRange={zoomRange}
              suppressEscape={isTrackMapFullscreen}
            />
          </div>
        </div>

        <StatusBar
          trackName={raceInfo?.track || 'Unknown'}
          trackLayout={raceInfo?.layout || ''}
          trackLength={trackLength}
          lapsLoaded={checkedLapIds.length + (hasActiveRef ? 1 : 0)}
        />
      </div>
  );
};

export default TelemetryPage;
